import produce from 'immer';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom-v5-compat';

import ReactSelect from 'dashboard/components/ReactSelect';
import { VerificationCheckConfiguration } from 'dashboard/forms/FormVerificationTemplateChecks/FormVerificationTemplateChecks';
import {
  HIGH_RISK_THRESHOLD_DESCRIPTION,
  LOW_RISK_THRESHOLD_DESCRIPTION,
} from 'dashboard/forms/FormVerificationTemplateChecks/RiskConfigConfigurationElement';
import { RiskLevel } from 'dashboard/models/verification-check';
import { SelfieLivenessCheckConfig } from 'dashboard/models/verification-check-config/selfie-liveness-detection';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';

interface SelfieLivenessConfigurationElementProps {
  checkConfig: SelfieLivenessCheckConfig;
  editable: boolean;
  handleUpdateCheck: (check: VerificationCheckConfig) => void;
}

const SelfieLivenessConfigurationElement = ({
  checkConfig,
  editable,
  handleUpdateCheck,
}: SelfieLivenessConfigurationElementProps) => {
  type RiskLevelConfig = {
    riskLevel: RiskLevel;
    normalized: number;
    raw: number;
  };

  // Note: the hard-coded raw scores here will be removed after migrating to normalized scores fully
  const riskLevelConfigs: RiskLevelConfig[] = [
    { riskLevel: RiskLevel.High, normalized: 90, raw: 0.0005 },
    { riskLevel: RiskLevel.Medium, normalized: 75, raw: 1 },
    { riskLevel: RiskLevel.Low, normalized: 50, raw: 25 },
  ];
  const knownNormalizedScores = Object.values(riskLevelConfigs).map((config) => config.normalized);

  const normalizedLivenessThreshold = checkConfig['normalized-liveness-threshold'];

  if (knownNormalizedScores.includes(normalizedLivenessThreshold)) {
    const currentRiskLevelConfig = riskLevelConfigs.find(
      (config) => config.normalized === normalizedLivenessThreshold,
    );
    const updateRiskTriggerLevel = (riskLevelConfig: RiskLevelConfig) => {
      const check = produce(checkConfig, (draftCheckConfig) => {
        draftCheckConfig['liveness-threshold'] = riskLevelConfig.raw;
        draftCheckConfig['normalized-liveness-threshold'] = riskLevelConfig.normalized;
      });
      handleUpdateCheck(check);
    };
    return (
      <VerificationCheckConfiguration
        title='Risk Level'
        helpText={
          <>
            <>{HIGH_RISK_THRESHOLD_DESCRIPTION}</>
            <br />
            <>{LOW_RISK_THRESHOLD_DESCRIPTION}</>
          </>
        }
        disabled={!editable}
      >
        <span>Selfie Liveness Risk Threshold</span>
        <ReactSelect
          options={riskLevelConfigs.map((riskLevelConfig) => ({
            label: startCase(riskLevelConfig.riskLevel),
            value: riskLevelConfig,
          }))}
          value={currentRiskLevelConfig}
          isDisabled={!editable}
          onChange={updateRiskTriggerLevel}
        />
      </VerificationCheckConfiguration>
    );
  } else {
    return (
      <VerificationCheckConfiguration title='Selfie liveness' disabled={!editable}>
        <p>
          Custom liveness configuration set. Please{' '}
          <Link to='/dashboard/contact-us'>contact us</Link> to adjust this configuration.
        </p>
      </VerificationCheckConfiguration>
    );
  }
};

export default SelfieLivenessConfigurationElement;
